import { PageController } from '@client/components/_core/layout/PageController';
import { SectionHeader } from '@client/components/_ui/data-display/SectionHeader';
import { StatusBadge } from '@client/components/_ui/display/StatusBadge';
import { InlineQueryError } from '@client/components/_ui/feedback/InlineQueryError';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { BackLink } from '@client/components/_ui/navigation/BackLink';
import { LodgementLinksTable } from '@client/components/lodgement/links/LodgementLinksTable';
import { usePermissions } from '@client/hooks/auth/usePermissions';
import { trpc } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Link2 } from 'lucide-react';

export const Route = createFileRoute(
    '/console/$orgId/configuration/claims-products/products-v2/details/$productId/lodgement-links',
)({
    staticData: {
        metadata: {
            title: 'Lodgement links',
        },
        ui: {
            icon: Link2,
            title: 'Lodgement links',
            subtitle: 'Public links that lodge claims against the published version of this product',
            menuDescription: 'Public claim lodgement links of a product',
        },
        presentation: 'fullPage',
        modules: ['claim'],
        permissions: ['settings:read'],
        featureFlag: 'is-products-v2-enabled',
    },
    component: RouteComponent,
});

function RouteComponent() {
    return (
        <PageController route={Route}>
            <ProductLodgementLinks />
        </PageController>
    );
}

function ProductLodgementLinks() {
    const { orgId, productId } = Route.useParams();
    const { can } = usePermissions();
    const product = trpc.product.getProduct.useQuery({ id: productId }, { meta: { errorSurface: 'inline' } });
    const versions = trpc.productVersion.listProductVersions.useQuery(
        { where: { productId }, orderBy: { version: 'desc' }, limit: 50 },
        { meta: { errorSurface: 'silent' } },
    );

    if (product.isPending) return <Loading />;
    if (product.error) return <InlineQueryError error={product.error} onRetry={() => void product.refetch()} />;

    const published = versions.data?.rows.find((version) => version.status === 'PUBLISHED');

    return (
        <div className="flex flex-col gap-4">
            <div>
                <BackLink to="/console/$orgId/configuration/claims-products/products-v2" params={{ orgId }}>
                    Back to products
                </BackLink>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <SectionHeader as="h2" title={product.data.name} className="mb-0" />
                    {published ? (
                        <StatusBadge
                            module="productVersion"
                            status="PUBLISHED"
                            customLabel={`Version ${published.version} published`}
                        />
                    ) : (
                        <StatusBadge module="productVersion" status="DRAFT" customLabel="No published version" />
                    )}
                </div>
                <p className="text-muted-foreground text-sm">
                    {product.data.Insurer?.name ? `Insurer ${product.data.Insurer.name} · ` : ''}A link always serves
                    the product's published version, so its URL never changes when a version is superseded.
                </p>
            </div>
            <LodgementLinksTable
                productId={productId}
                productName={product.data.name}
                canWrite={can('settings:update')}
            />
        </div>
    );
}
